import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Paper, Button, CircularProgress, Divider } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useNotification } from "../../providers/NotificationProvider";
import { fetchSmInfoCapitalGoalsApp } from "../../api/memberApi";

function DetailRow({ label, value }) {
  return (
    <Box sx={{ display: "flex", py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography variant="body2" sx={{ width: 200, flexShrink: 0, color: "text.secondary", fontWeight: 500 }}>{label}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{value || "—"}</Typography>
    </Box>
  );
}

export default function SmInfoCapitalGoalAppDetailPage() {
  const { businessDomainId, successFactorId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSmInfoCapitalGoalsApp(businessDomainId);
      const found = (Array.isArray(data) ? data : []).find(
        (g) => String(g.successFactorId) === String(successFactorId)
      );
      setItem(found || null);
    } catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
  }, [businessDomainId, successFactorId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}><CircularProgress /></Box>;
  if (!item) return null;

  const indicators = item.indicators || [];
  const sources = item.sources || [];

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/design/${businessDomainId}/strategy-map/info-capital-goals-app`)} variant="text">
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>{t("sm.infoCapitalGoalApp.detail.title")}</Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <DetailRow label={t("common.name")} value={item.successFactorName} />
        <DetailRow label={t("common.description")} value={item.successFactorDescription} />
        {item.applicationTypeId && (
          <DetailRow label={t("tree.applicationTypes")} value={item.applicationTypeId} />
        )}
      </Paper>

      {indicators.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{t("type.indicators")}</Typography>
          <Divider sx={{ mb: 1.5 }} />
          {indicators.map((ind, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
              ・{ind.indicatorName}{ind.unit ? `（${ind.unit}）` : ""}
            </Typography>
          ))}
        </Paper>
      )}

      {sources.length > 0 && (
        <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{t("sm.sourcesLabel")}</Typography>
          <Divider sx={{ mb: 1.5 }} />
          {sources.map((s, i) => (
            <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
              ・{s.successFactorName}
            </Typography>
          ))}
        </Paper>
      )}
    </Box>
  );
}
